import { useDeferredValue } from 'react'

/**
 * Props for the CircularProgress component
 */
export type CircularProgressProps = {
  /** Progress value between 0 and 100.
   *
   * Default: `0` */
  value?: number
  /** Whether to show an indeterminate loading state.
   *
   * Default: `false` */
  indeterminate?: boolean
}

/**
 * A circular progress indicator component that displays progress as a circular arc.
 * Can be used in either determinate mode (with a specific value) or indeterminate mode (for loading states).
 *
 * @param props - Component props
 * @returns A circular progress indicator
 */
export function CircularProgress(props: CircularProgressProps) {
  const { value = 0, indeterminate = false } = props

  const deferredValue = useDeferredValue(value)
  const percent = Math.min(100, Math.max(0, deferredValue))

  return (
    <svg
      role="progressbar"
      aria-label={`${percent} percent`}
      style={{
        // @ts-ignore
        '--size': '48px',
        '--thickness': '4px',
        '--radius': 'calc(var(--size) / 2 - var(--thickness) / 2)',
        '--percent': percent,
        width: 'var(--size)',
        height: 'var(--size)',
        transition: 'all 0.3s',
        animation: indeterminate ? 'spin 2s linear infinite' : 'none',
      }}
      className="circular-progress"
    >
      <circle
        style={{
          // @ts-ignore
          '--gap':
            'calc(0.085 * min(1, min(1, max(0, round(var(--percent) - 1)))) + 0 * (1 - min(1, max(0, round(var(--percent) - 1)))))',
          '--circumference': 'calc(2 * 3.14159 * var(--radius))',
          '--offset': 'calc(var(--circumference) * (1 + var(--gap) - (100 - var(--percent)) / 100))',
          cx: 'calc(var(--size) / 2)',
          cy: 'calc(var(--size) / 2)',
          r: 'var(--radius)',
          fill: 'transparent',
          strokeWidth: 'var(--thickness)',
          strokeDashoffset: 'calc(var(--circumference) * (1 + var(--gap) - (100 - var(--percent)) / 100))',
          strokeDasharray: 'var(--circumference)',
          transformOrigin: 'center',
          transform: 'rotate(calc((360deg * (1 - (100 - var(--percent)) / 100)) - 75deg))',
          transition: 'all 0.3s',
          opacity: indeterminate ? '0' : '1',
        }}
        className="circular-progress__track stroke-brand-purple-200"
        strokeLinecap="round"
      ></circle>
      <circle
        style={{
          // @ts-ignore
          '--circumference': 'calc(2 * 3.14159 * var(--radius))',
          '--offset': 'calc(var(--circumference) * (100 - var(--percent)) / 100)',
          cx: 'calc(var(--size) / 2)',
          cy: 'calc(var(--size) / 2)',
          r: 'var(--radius)',
          fill: 'transparent',
          strokeWidth: 'var(--thickness)',
          strokeDashoffset: 'calc(var(--circumference) * ((100 - var(--percent)) / 100))',
          strokeDasharray: 'var(--circumference)',
          transformOrigin: 'center',
          transform: 'rotate(-90deg)',
          transition: 'all 0.3s',
          animation: indeterminate ? 'circular-progress 1.5s linear infinite' : 'none',
        }}
        className="circular-progress__range stroke-brand-purple-700"
        strokeLinecap="round"
      ></circle>
    </svg>
  )
}
